import { filter } from 'rxjs/operators';

import { ChangeDetectionStrategy, Component, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { WIZ_EVENT_TYPE, VALUE_ENTER, VALUE_ESCAPE } from '@wizeoo/akm-core-lib';
import {
  WizBasePopUp,
  WizCheckbox,
  WizDatasource,
  WizDropdown,
  WizEventManager, WizEvent,
  WizNumericInput,
  WizTextInput
} from '@wizeoo/akm-component-lib';
import { Subscription } from 'rxjs';
import { WidgetConfigurationObj } from '@wizeoo/akm-message-lib';

import { WidgetDataPopDatas } from '../../../manager/widgets/i-widget';
import { WIDGET_KIND } from '../../../model/enums';
import { GridsterWidget } from '../../../vo/workingzone/gridster-widget';
import { Model } from '../../../model/model';
import { VisionStepVO } from '../../../vo/vision/visionstep.vo';
import { COMPUTE_MODE } from '@wizeoo/akm-compute-lib';

/**
 * Pop de sélection du périmètre de données du widget (Vision, vision step, step perimeter)
 */
@Component({
  selector: 'widget-data-pop',
  templateUrl: './widget-data.pop.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetDataPop extends WizBasePopUp implements OnDestroy {
  
    // validation sur enter, sortie sur Esc
    @HostListener('window:keydown', ['$event'])
    keyEvent(event: KeyboardEvent) {
      if (event.key === VALUE_ENTER && this.validable) {
        this.save();
      }
      if (event.key === VALUE_ESCAPE) {
        this.hide();
      }
    }
  
  // dimensions demandées
  public static width: string = '520px';
  public static height: string = '400px';
    
  @ViewChild('ddMaster', { read: WizDropdown }) ddMaster: WizDropdown; // drop du master
  @ViewChild('cbMefe', { read: WizCheckbox }) cbMefe: WizCheckbox; // cb pas de données sur l'esclave si pas de données sur le maître
  @ViewChild('cbTot', { read: WizCheckbox }) cbTot: WizCheckbox; // affichage du total pour les grids / pivots
  @ViewChild('cbSubTot', { read: WizCheckbox }) cbSubTot: WizCheckbox; // affichage du sous-total pour les pivots
  @ViewChild('cbSubTotMulti', { read: WizCheckbox }) cbSubTotMulti: WizCheckbox; // affichage du sous-total pour les pivots, multiples
  @ViewChild('cbComputeMode', { read: WizCheckbox }) cbComputeMode: WizCheckbox; // calculs COMPUTE
  @ViewChild('niPageSize', { read: WizNumericInput }) niPageSize: WizNumericInput;
  @ViewChild('tiPmdExParams', { read: WizTextInput }) tiPmdExParams: WizTextInput;

  /** La datasource de choix de visionStep (de la vision) */
  stepDS: WizDatasource<VisionStepVO> = new WizDatasource<VisionStepVO>()
    .setSortFields([{ property: 'level', way: 'asc', numeric: true }])
    .build();


  /** tous les widgets éligibles comme 'Master' */
  public allAgis: WizDatasource<GridsterWidget> = new WizDatasource<GridsterWidget>([])
    .setItemCodeField('uid')
    .setSortFields([{ property: 'indice', way: 'asc', numeric: true }])
    .build();

  public currentMaster: GridsterWidget = null; // le maître en cours de sélection

  private _subs: Subscription[] = []; // listener pour garantir l'unicité de sélection

  public showTotal: boolean = false;
  public showSubTotal: boolean = false;
  public showSubTotalMulti: boolean = false;

  public pmdExParams: string = null;

  // calcul en mode compute
  public computeMode: boolean = false;
  // taille de page par défaut
  public computePageSize: number = 100;


  public show(): void {
    this.feedSteps(); // alimentation des steps (1x)
    this.feedMasterDS(); // alimentation de la drop des maîtres (chaque changement de step)

    this.setListeners();
  }

  /** pose des écouteurs  */
  private setListeners(): void {
    WizEventManager.addListener(this, this.ddMaster, WIZ_EVENT_TYPE.SELECTION_CHANGED, this.setMaster); // choix d'un master
    if(this.cbComputeMode) {
      WizEventManager.addListener(this, this.cbComputeMode, WIZ_EVENT_TYPE.MODEL_UPDATE, this.setComputeMode); // choix du mode de calcul
    }
    WizEventManager.addListener(this, this.niPageSize, WIZ_EVENT_TYPE.MODEL_UPDATE, this.setPageSize); // taille de page
    this._subs.push(
      this.stepDS.changes.pipe(filter((c) => c.type === 'selectionChanged')).subscribe((c) => {
        this.feedMasterDS(); // alimentation de la drop des maîtres (chaque changement de step)
      })
    );

  }

  ngOnDestroy() {
    WizEventManager.removeListener(this, this.ddMaster, WIZ_EVENT_TYPE.SELECTION_CHANGED); // choix d'un master
    if(this.cbComputeMode) {
      WizEventManager.removeListener(this, this.cbComputeMode, WIZ_EVENT_TYPE.MODEL_UPDATE);
    } 
    WizEventManager.removeListener(this, this.niPageSize, WIZ_EVENT_TYPE.MODEL_UPDATE); // taille de page
    this._subs.forEach((sub) => sub.unsubscribe());
  }

  public datas: WidgetDataPopDatas;

  public get model(): Model {
    return this.datas.model as Model;
  }

  /** accesseur sur le data avec le bon type */
  private get wco(): WidgetConfigurationObj {
    return this.datas.config;
  }

  /** accesseur sur la conf gridster */
  private get gritVO(): GridsterWidget {
    return this.datas.gridsterConfig;
  }


  public get validable(): boolean {
    return !!this.stepDS.selectedItem;
  }

  /**
   * génération de la collection de step pour alimentation de la drop
   * et alimentation du grid si le contexte est alimenté
   */
  private feedSteps(): void {
    let stepArr: VisionStepVO[] = [];
    // le data est un widgetC
    const visionCode = this.wco.v;
    const vision =  this.model.visionEngine.getVision(visionCode);
    if (!!vision && !!vision.visionSteps) {
      stepArr = Object.values(vision.visionSteps).map((visionStep) => new VisionStepVO(visionStep));
    }
    this.stepDS.itemCodeField = 'visionAndStep';
    this.stepDS.setSource(stepArr);

    if (this.wco.vs) {
      // visionStep défini
      const selectedVS: VisionStepVO = stepArr.find((vs) => vs.level === Number(this.wco.vs));
      if (!!selectedVS) {
        this.stepDS.select([selectedVS]);
      }
    }

    if (this.wco.muid) {
      // maître alimenté
      this.currentMaster =  this.model.widgetEngine.getWidgetByUid(this.wco.muid);
    }

    if(this.wco.stot) {
      this.showTotal = true;
    }

    if(this.wco.pmd) {
      this.showSubTotal = true;
    }

    if(this.wco.pmdex) {
      this.showSubTotalMulti = true;
    }

    if(this.wco.pmdexParams) {
      this.pmdExParams = this.wco.pmdexParams;
    }

    if(this.wco.cm === COMPUTE_MODE.COMPUTE) {
      this.computeMode = true;
    }

    if(this.wco.cms) {
      this.computePageSize = this.wco.cms;
    }
  }

  /** alimentation de la DS des maîtres possibles */
  private feedMasterDS(): void {
    this.allAgis.setSource(
       this.model.widgetEngine.availableMasters(
         this.model.workingTemplateClone.widgets,
        this.gritVO,
        this.stepDS.selectedItem?.source)
    ); // alimentation de la DS des Masters possibles TODO6

    if (!!this.currentMaster) {
      // on prend l'instance de la collection
      this.currentMaster = this.allAgis.getItemByKey(this.currentMaster.uid);
    }

    if (!!this.currentMaster) {
      // il est dedans
      this.allAgis.select([this.currentMaster]);
    } else {
      this.allAgis.clearSelection();
    }
  }

  /** changement de sélection depuis la drop de master */
  private setMaster(e: WizEvent): void {
    this.currentMaster = this.ddMaster.selectedItem;
  }

  private setComputeMode(e: WizEvent): void {
    this.computeMode = this.cbComputeMode.wizChecked;
  }

  private setPageSize(e: WizEvent): void {
    this.computePageSize = this.niPageSize.wizNumber;
  }

  /** sur save, alimentation de l'objet de configuration */
  public save(): void {
    let stepChanged: boolean = false;
    const previousLevel: number = Number(this.wco.vs);

    this.wco.vs = this.stepDS.selectedItem ? this.stepDS.selectedItem.level : null;
    this.wco.muid = this.allAgis.selectedItem ? this.allAgis.selectedItem.uid : null; // on prend l'uid du master
    this.wco.mefe = this.cbMefe.wizChecked;
    this.wco.stot = this.cbTot?.wizChecked || null;
    this.wco.pmd = this.cbSubTot?.wizChecked || null;
    this.wco.pmdex = this.cbSubTotMulti?.wizChecked || null;
    this.wco.pmdexParams = this.tiPmdExParams?.wizValue || null;
    this.wco.cm = this.cbComputeMode?.wizChecked ? COMPUTE_MODE.COMPUTE : COMPUTE_MODE.LOCAL;
    this.wco.cms = this.computePageSize;


    if (previousLevel !== Number(this.wco.vs)) {
      stepChanged = true;
    }

    this.callback(this.wco, stepChanged);
    this.hide();
  }

  /** présence d'un master pour checkbox de choix de mode */
  public get hasMaster(): boolean {
    return !!this.allAgis.selectedItem;
  }

  public get emptyToEmpty(): boolean {
    return this.wco.mefe;
  }

  /** grid ou pivot ? */
  public get isGrid(): boolean {
    return this.wco.k === WIDGET_KIND.GRID || this.wco.k === WIDGET_KIND.PIVOT;
  }

  /** pivot ? */
  public get isPivot(): boolean {
    return this.wco.k === WIDGET_KIND.PIVOT;
  }

  public manageSettings(e: WizEvent): void {
    this.showSubTotalMulti = e.value;
    // console.log('manageSettings', e, this.showSubTotalMulti)
  }

  /** pivot ? */
  public get isPmdEx(): boolean {
    return this.showSubTotalMulti === true;
  }

}
